import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { MessageCircle, Home as HomeIcon } from 'lucide-react';
import { mockData, generateWhatsAppLink } from '../mock';
import PaymentSuccessHero from '../components/payment-success/PaymentSuccessHero';
import PaymentDetailsCard from '../components/payment-success/PaymentDetailsCard';
import NextStepsCard from '../components/payment-success/NextStepsCard';

const WHATSAPP_DELAY_MS = 2000;

const PaymentSuccess = () => {
  const location = useLocation();
  const state = location.state || {};
  const {
    payment_id,
    order_id,
    customer,
    program,
    amount,
    installment,
    installment_number,
  } = state;

  useEffect(() => {
    if (!payment_id || !program?.name) return undefined;

    const message = `Hi! I've successfully enrolled in ${program.name}. Payment ID: ${payment_id}. Looking forward to starting my journey!`;
    const link = generateWhatsAppLink(message);
    const timer = setTimeout(() => {
      window.open(link, '_blank', 'noopener,noreferrer');
    }, WHATSAPP_DELAY_MS);
    return () => clearTimeout(timer);
  }, [payment_id, program?.name]);

  if (!payment_id) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#0A0F1C]"
        data-testid="payment-success-invalid"
      >
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Invalid Access</h1>
          <Link
            to="/"
            className="text-brand-primary hover:text-brand-secondary"
            data-testid="payment-success-home-link"
          >
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[#0A0F1C]"
      style={{ paddingTop: '100px' }}
      data-testid="payment-success-page"
    >
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">
          <PaymentSuccessHero />

          <PaymentDetailsCard
            payment_id={payment_id}
            order_id={order_id}
            program={program}
            amount={amount}
            installment={installment}
            installment_number={installment_number}
          />

          <div
            className="network-card p-8 mb-8"
            data-testid="payment-success-customer-card"
          >
            <h3 className="text-xl font-bold text-white mb-4">Your Details</h3>
            <div className="space-y-3">
              <div>
                <p className="text-text-muted text-sm">Name</p>
                <p className="text-white">{customer?.name}</p>
              </div>
              <div>
                <p className="text-text-muted text-sm">Email</p>
                <p className="text-white">{customer?.email}</p>
              </div>
              <div>
                <p className="text-text-muted text-sm">Phone</p>
                <p className="text-white">{customer?.phone}</p>
              </div>
            </div>
          </div>

          <NextStepsCard />

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={generateWhatsAppLink(
                `Hi! I've enrolled in ${program?.name}. Payment ID: ${payment_id}`
              )}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-cta inline-flex items-center justify-center gap-2"
              data-testid="payment-success-whatsapp-btn"
            >
              <MessageCircle size={20} aria-hidden="true" />
              Chat with Us on WhatsApp
            </a>

            <Link
              to="/"
              className="btn-secondary inline-flex items-center justify-center gap-2"
              data-testid="payment-success-back-home-btn"
            >
              <HomeIcon size={20} aria-hidden="true" />
              Back to Home
            </Link>
          </div>

          <div className="text-center mt-8">
            <p className="text-text-muted text-sm mb-2">Need help?</p>
            <div className="flex items-center justify-center gap-4 text-sm">
              <a
                href={`mailto:${mockData.contact.email}`}
                className="text-brand-primary hover:text-brand-secondary"
              >
                {mockData.contact.email}
              </a>
              <span className="text-text-muted">|</span>
              <a
                href={`tel:${mockData.contact.phone}`}
                className="text-brand-primary hover:text-brand-secondary"
              >
                {mockData.contact.phone}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
